import {
  Button,
  CSSObject,
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Divider,
  Stack,
  Tabs as ChakraTabs,
  TabsProps as ChakraTabsProps,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai/index.js';

export interface TabProps {
  label: React.ReactNode;
  key: string;
  content: React.ReactNode;
  itemProps?: CSSObject;
  closable?: boolean;
  isDisabled?: boolean;
}

export interface TabsProps
  extends Omit<ChakraTabsProps, 'children' | 'onChange'> {
  items: TabProps[];
  onChange?: (val: string) => void;
  type?: 'default' | 'editable';
  onEdit?: (type: 'add' | 'remove', key?: string) => void;
  hideAdd?: boolean;
  removeIcon?: React.ReactElement;
  addIcon?: React.ReactNode;
  addButtonProps?: CSSObject;
  removeButtonProps?: CSSObject;
  tabLabelProps?: CSSObject;
  tabListProps?: CSSObject;
  tabPanelProps?: CSSObject;
  tabPanelsProps?: CSSObject;
  tabProps?: CSSObject;
  tabsProps?: ChakraTabsProps;
  tabPosition?: 'top' | 'bottom' | 'left' | 'right';
  applyBorderRadiusForStartEndButtons?: boolean;
  borderRadiusForStartEndButtons?: string | number;
  addDivider?: boolean;
}

export const Tabs = (props: TabsProps) => {
  const {
    items = [],
    type = 'info',
    onEdit,
    hideAdd = false,
    removeIcon,
    addIcon,
    addButtonProps,
    removeButtonProps: _removeButtonProps,
    tabLabelProps,
    tabListProps,
    tabPanelProps,
    tabPanelsProps,
    tabProps,
    tabsProps,
    onChange,
    applyBorderRadiusForStartEndButtons,
    borderRadiusForStartEndButtons,
    tabPosition = 'top',
    addDivider = false,
    ...rest
  } = props;
  const onTabSelected = (index: number) => {
    onChange && onChange(items[index].key as string);
  };
  const styles = useMultiStyleConfig('Tabs', props);

  const removeTabIcon = removeIcon || <AiOutlineClose />;
  const addTabIcon = addIcon || (
    <Icon as={AiOutlinePlus} sx={{ ...styles.ActionIcon }} />
  );
  const getDirectionFromPosition = (tabPosition: TabsProps['tabPosition']) => {
    switch (tabPosition) {
      case 'top':
        return 'column';
      case 'bottom':
        return 'column-reverse';
      case 'left':
        return 'row';
      case 'right':
        return 'row-reverse';
      default:
        return 'column';
    }
  };

  return (
    <ChakraTabs
      onChange={onTabSelected}
      sx={{
        ...styles.Tabs,
        ...tabsProps,
        flexDirection: getDirectionFromPosition(tabPosition),
      }}
      {...rest}
    >
      <TabList
        sx={{
          ...styles.TabList,
          ...tabListProps,
          flexDirection:
            tabPosition === 'top' || tabPosition === 'bottom'
              ? 'row'
              : 'column',
        }}
      >
        {items.map((item, index) => {
          const getStartEndBorderProps = () => {
            return applyBorderRadiusForStartEndButtons
              ? {
                  borderLeftRadius:
                    index === 0 ? borderRadiusForStartEndButtons : 0,
                  borderRightRadius:
                    index === items.length - 1
                      ? borderRadiusForStartEndButtons
                      : 0,
                }
              : {};
          };
          return (
            <Tab
              sx={{
                ...styles.Tab,
                ...tabProps,
                ...item.itemProps,
                ...getStartEndBorderProps(),
              }}
              value={item.key}
              key={item.key || index}
              isDisabled={item.isDisabled}
              p={0}
              pl={2}
              pr={2}
            >
              <Flex flexDir={'row'} p={0}>
                <Text sx={{ ...styles.TabLabel, ...tabLabelProps }}>
                  {item.label}
                </Text>
                {type === 'editable' && item.closable && (
                  <Button
                    variant='unstyled'
                    size='sm'
                    rightIcon={removeTabIcon}
                    mt={1}
                    onClick={() => onEdit && onEdit('remove', item.key)}
                  ></Button>
                )}
              </Flex>
            </Tab>
          );
        })}
        {type === 'editable' && !hideAdd && (
          <Button
            sx={{ ...styles.AddButton, ...addButtonProps }}
            onClick={() => onEdit && onEdit('add')}
          >
            {addTabIcon}
          </Button>
        )}
      </TabList>
      {addDivider && (
        <Stack
          sx={
            tabPosition === 'top' || tabPosition === 'bottom'
              ? { pt: 4, pb: 4 }
              : { pl: 4, pr: 4 }
          }
        >
          <Divider
            borderColor='gray.400'
            orientation={
              tabPosition === 'top' || tabPosition === 'bottom'
                ? 'horizontal'
                : 'vertical'
            }
          />
        </Stack>
      )}
      <TabPanels sx={{ ...styles.TabPanels, ...tabPanelsProps }}>
        {items.map((item, index) => (
          <TabPanel
            sx={{ ...styles.TabPanel, ...tabPanelProps, ...item.itemProps }}
            key={item.key || index}
          >
            {item.content}
          </TabPanel>
        ))}
      </TabPanels>
    </ChakraTabs>
  );
};
